import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Vote, CheckCircle2, Clock, CalendarDays, HelpCircle, BarChart3 } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import api from '../../api/axios';
import styles from './ResidentPolls.module.css';

const ResidentPolls = () => {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPolls();
  }, []);

  const fetchPolls = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/community/polls');
      
      // Map userVoted from the voters array
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const mappedPolls = data.map(poll => {
        const hasVoted = poll.voters.includes(userInfo._id);
        // We can't know which option they voted for without a more complex schema, 
        // so we'll just set it to true if they voted.
        return { ...poll, userVoted: hasVoted ? 'voted' : null };
      });
      
      setPolls(mappedPolls);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };
  const handleVote = async (pollId, optionId) => {
    try {
      const { data } = await api.post(`/community/polls/${pollId}/vote`, { optionId });
      
      setPolls(polls.map(poll => {
        if (poll._id === pollId) {
          return {
            ...data,
            userVoted: 'voted'
          };
        }
        return poll;
      }));
      toast.success('Vote cast successfully!');
    } catch (err) {
      toast.error("Failed to cast vote. You may have already voted.");
    }
  };

  return (
    <div className={styles.container}>
      <PageHeader 
        title="Polls & Voting" 
        breadcrumb={['Community', 'Polls']} 
      />

      <div className={styles.grid}>
        {loading ? <p>Loading polls...</p> : polls.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem 2rem', background: '#fff', borderRadius: '12px', border: '1px dashed #d1d5db', gridColumn: '1 / -1' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', borderRadius: '50%', background: '#eff6ff', color: '#1d4ed8', marginBottom: '1rem' }}>
              <BarChart3 size={32} />
            </div>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#111', fontSize: '18px' }}>No Active Polls</h3>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>There are currently no active polls or voting events for the society.</p>
          </div>
        ) : polls.map(poll => {
          const isActive = poll.status === 'Active';
          const hasVoted = !!poll.userVoted;
          const showResults = hasVoted || !isActive;

          return (
            <div key={poll._id} className={`${styles.card} ${!isActive ? styles.closedCard : ''}`}>
              <div className={styles.cardHeader}>
                <div className={styles.titleArea}>
                  <HelpCircle size={20} className={styles.icon} />
                  <h3>{poll.title}</h3>
                </div>
                <span className={`${styles.badge} ${isActive ? styles.badgeActive : styles.badgeClosed}`}>
                  {isActive ? <Clock size={12} /> : <CheckCircle2 size={12} />}
                  {poll.status}
                </span>
              </div>
              
              <p className={styles.description}>{poll.description}</p>
              
              <div className={styles.meta}>
                <span><CalendarDays size={14} /> Ends on: {poll.endDate}</span>
                <span><Vote size={14} /> {poll.totalVotes} Total Votes</span>
              </div>

              <div className={styles.optionsList}>
                {poll.options.map(opt => {
                  const percentage = poll.totalVotes > 0 
                    ? Math.round((opt.votes / poll.totalVotes) * 100) 
                    : 0;
                  
                  const isSelected = poll.userVoted === opt.id;
                  const visibleVoters = opt.voters ? opt.voters.slice(0, 3) : [];
                  
                  return (
                    <div 
                      key={opt.id} 
                      className={`${styles.waOption} ${isSelected ? styles.waOptionSelected : ''}`}
                      onClick={() => {
                        if (!hasVoted && isActive) {
                          handleVote(poll._id, opt.id);
                        }
                      }}
                      style={{ cursor: (!hasVoted && isActive) ? 'pointer' : 'default' }}
                    >
                      {/* Background Progress Bar */}
                      {showResults && (
                        <div 
                          className={styles.waProgressBg} 
                          style={{ width: `${percentage}%` }}
                        ></div>
                      )}
                      
                      <div className={styles.waOptionContent}>
                        <div className={styles.waLeftArea}>
                          <div className={`${styles.waRadio} ${isSelected ? styles.waRadioSelected : ''}`}></div>
                          <span className={styles.waText}>{opt.text}</span>
                        </div>
                        
                        {showResults && (
                          <div className={styles.waRightArea}>
                            <div className={styles.waAvatars}>
                              {visibleVoters.map((v, i) => {
                                const imgUrl = v.profileImage ? (v.profileImage.startsWith('http') ? v.profileImage : `${import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '')}${v.profileImage}`) : null;
                                return (
                                  <div key={i} className={styles.waAvatar}>
                                    {imgUrl && (
                                      <img 
                                        src={imgUrl} 
                                        alt="" 
                                        style={{width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover'}}
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.nextSibling.style.display = 'flex';
                                        }}
                                      />
                                    )}
                                    <span style={{ display: imgUrl ? 'none' : 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                                      {v.fullName ? v.fullName.charAt(0).toUpperCase() : 'U'}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                            <span className={styles.waVoteCount}>{opt.votes > 0 ? opt.votes : ''}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {hasVoted && isActive && (
                <div className={styles.votedMessage}>
                  <CheckCircle2 size={16} /> You have cast your vote for this poll.
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ResidentPolls;
